import React from "react";
import jsPDF from "jspdf";
// eslint-disable-next-line
import lodashGet from "lodash/get";

import ImageEye from "@material-ui/icons/RemoveRedEye";

import { toFixed, isEmpty, getDistinctValues, toFixedSigned, toFixedValue, isTrue } from "../../utils/helpers";
import { cbLayRowSort, getCutOrderFlowInfoList } from "../../helpers/orderHelper";
import { setDefault, minReqHeight, totalPagesExp, addTable } from "../../helpers/pdfHelper";
import { SpreadingType, LayType, RollType } from "../../utils/types";
import { MiscType, REMNANT_LAY, rollPlaneType } from "../../constants";

export const DataType = {
  LAY_SLIP: "LaySlip",
  LAY: "Lay",
  MARKER: "Marker"
};

export const getHeader = ({ dataType, orderFlow, setting, print = false }) => {
  let columns = [];
  if (dataType === DataType.LAY_SLIP) {
    columns = [
      { dataKey: "sizeGroup", title: "Size Group", align: "center", rowSpan: ({ record }) => (record.serialNo ? record.layCount : 0) },
      { dataKey: "marker", title: "Marker", rowSpan: ({ record }) => (record.serialNo ? record.layCount : 0) },
      { dataKey: "layLength", title: "Lay Length", align: "center", rowSpan: ({ record }) => (record.serialNo ? record.layCount : 0) },
      { dataKey: "way", title: "Way", align: "center", rowSpan: ({ record }) => (record.serialNo ? record.layCount : 0) },
      { dataKey: "layNo", title: "Lay No", align: "center" },
      { dataKey: "plies", title: "Plies", align: "center" },
      { dataKey: "rollCount", title: "No. Of Rolls", align: "center" }
    ];
    if (orderFlow && orderFlow.orderConfig && orderFlow.orderConfig.layType) {
      columns.push({ dataKey: "action", title: "Action", align: "center" });
    }
  } else if (dataType === DataType.LAY) {
    columns = [
      { dataKey: "serialNo", title: "Sl. No." },
      { dataKey: "rollNo", title: "F. Roll No." },
      { dataKey: "supplierRollNo", title: "S. Roll No." },
      { dataKey: "style", title: "Style" },
      { dataKey: "color", title: "Color" },
      { dataKey: "rollWeight", title: "Weight" },
      { dataKey: "gsm", title: "GSM" },
      { dataKey: "rollLength", title: "Length" },
      { dataKey: "width", title: "Width" },
      { dataKey: "shade", title: "Shade" },
      { dataKey: "plannedPly", title: "Plies" },
      { dataKey: "plannedBalance", title: "Balance" },
      { dataKey: "next", title: "Next" }
    ];
    if (orderFlow?.styleList?.length <= 1) {
      columns = columns.filter(c => c.dataKey !== "style");
    }
    if (orderFlow?.colorList?.length <= 1) {
      columns = columns.filter(c => c.dataKey !== "color");
    }
    if (print) {
      columns = columns.concat([
        { dataKey: "aPly", title: "Actual Ply" },
        { dataKey: "aBalance", title: "Actual Balance" },
        { dataKey: "causeDeviation", title: "Reason" }
      ]);
    }
  } else if (dataType === DataType.MARKER) {
    columns = [
      { dataKey: "sizeGroup", title: "Size Group" },
      { dataKey: "marker", title: "Marker" },
      { dataKey: "way", title: "Way" },
      { dataKey: "shrinkage", title: "Shrinkage" },
      { dataKey: "width", title: "Width" },
      { dataKey: "markerWidth", title: "Marker \nWidth" },
      { dataKey: "layWidth", title: "Lay \nWidth" },
      { dataKey: "markerLength", title: "Marker \nLength" },
      { dataKey: "layLength", title: "Lay \nLength" },
      { dataKey: "plies", title: "No. Of Plies" }
    ];
    const isCPL = !isEmpty(setting?.others) && Array.isArray(setting.others) && setting.others.includes(MiscType.CPL);
    if (isCPL) {
      columns = columns.filter(c => c.dataKey !== "width");
    } else {
      columns = columns.filter(c => !["markerWidth", "layWidth"].includes(c.dataKey));
    }
  }
  return columns;
};

export const getData = ({ dataType, layMap, layTab, onViewClick, lay, orderFlow, extraRolls = [], rollList = [] }) => {
  let rows = [];
  if (dataType === DataType.LAY_SLIP && layMap && layTab && orderFlow) {
    let layList = layMap[layTab];
    if (layList && layList.length > 0) {
      layList = layList.sort((a, b) => a.layNo - b.layNo);

      let markerSet = new Set();
      let markerNo = 1;
      rows = layList.map((lay, idx) => {
        const { marker, layNo, rowList } = lay;
        const plies = rowList?.reduce((acc, e) => acc + e.plannedPly, 0) || 0;
        const fabricQty = rowList && rowList.reduce((acc, row) => acc + marker.layLength * row.plannedPly, 0);
        let serialNo;
        if (!markerSet.has(marker.id)) {
          serialNo = markerNo++;
          markerSet.add(marker.id);
        }
        const sizeGroup = getDistinctValues(marker.name?.split(", ").map(e => e.split("_")[0]));
        const markerName = marker.name
          ?.split(", ")
          .map(e => e.split("_")[1])
          .join(", ");

        return {
          serialNo,
          sizeGroup,
          marker: markerName,
          // groupName: getGroupName(marker.group),
          layLength: marker.layLength,
          way: marker.way,
          layNo,
          layCount: layList.filter(lay => marker.id === lay.marker.id).length,
          plies,
          rollCount: rowList && rowList.length,
          fabricQty: toFixed(fabricQty, 1),
          action: <ImageEye onClick={onViewClick && onViewClick(lay)} />
        };
      });
    }
  } else if (dataType === DataType.LAY && lay) {
    const { marker, rowList } = lay;

    rows = rowList.sort(cbLayRowSort).map(row => {
      const rolls = rollList.filter(e => row.rollNo === e.rollNo) || [];
      return {
        ...row,
        plannedBalance: `${row.plannedBalance}`,
        userRemark: getDistinctValues(rolls.map(e => e?.userRemarks))?.join(",") || "",
        active: rolls.some(e => isTrue(e.active)),
        rollNo: `${row.rollNo}${extraRolls.includes(row.rollNo.split("-")[0]) ? ` *` : ""}`,
        layLength: marker.layLength,
        rollLength: `${toFixedValue(row.currLength)} m (${toFixedValue(row.length)} m)`,
        rollWeight:
          !isEmpty(row.currWeight) && !isEmpty(row.weight) ? `${toFixedValue(row.currWeight, 3)} kg (${toFixedValue(row.weight, 3)} kg)` : "",
        next: row.next || "-"
      };
    }).map(e => ({...e, causeDeviation: e.active ? e.causeDeviation : 'Returned'}));

    const keys = ["currLength", "currWeight", "plannedPly"];
    const totalRow = keys.reduce(
      (acc, key) => ({
        ...acc,
        [key]: toFixed(
          rows.reduce((acc, row) => acc + parseFloat(row[key] || 0), 0),
          2
        )
      }),
      {}
    );
    rows.push({
      ...totalRow,
      rollLength: `${toFixed(totalRow.currLength, 1)} m`,
      rollWeight: totalRow.currWeight > 0 ? `${toFixed(totalRow.currWeight, 1)} kg` : "",
      rollNo: "Total"
    });
  }
  return rows;
};

export const printLayslip = ({
  order,
  orderFlow,
  layTab,
  rollList = [],
  messages,
  tenant,
  flowInfoFields = [],
  orderFields = [],
  rollFields = [],
  i18nMessages = {},
  unit = {},
  type = "standard"
}) => {
  if (order && orderFlow && orderFlow.orderConfig) {
    var doc = new jsPDF();

    setDefault(doc, "Roll Plan", tenant, null, unit);
    doc.setFontSize(10);

    const pageHeight = doc.internal.pageSize.getHeight();
    let startY = 24;

    ////// Print Order Info //////////////

    const setting = orderFlow.orderConfig.setting || {};

    const orderInfo = layOrderInfoData(order, orderFlow);
    const fields = [...orderFields, ...flowInfoFields, "partName", "lot"];
    let columns = orderInfo.columns
      .filter(c => fields.includes(c.dataKey))
      .map(c => ({
        ...c,
        header: orderFields.includes(c.dataKey)
          ? lodashGet(i18nMessages, `resources.orders.fields.${c.dataKey}`, c.header)
          : lodashGet(i18nMessages, `resources.orders.fields.flowInfo.${c.dataKey}`, c.header)
      }));

    const tableWidth = doc.internal.pageSize.getWidth() - 14;

    var autoTableOptions = {
      startY: startY + 10,
      body: orderInfo.body,
      columns
    };

    doc.text("Order Details", 7, startY + 7);
    addTable(doc, autoTableOptions, { tableWidth, minCharLen: 11 });

    let layList;
    if (layTab) {
      if (layTab === "Remnant Lay") {
        if (orderFlow.orderConfig.reGeneratedLaySlip) {
          layList = JSON.parse(orderFlow.orderConfig.reGeneratedLaySlip);
        } else {
          const layMap = JSON.parse(orderFlow.orderConfig.laySlip);
          layList = layMap["Remnant Lay"];
        }
      } else {
        const layMap = JSON.parse(orderFlow.orderConfig.laySlip);
        layList = layMap[layTab];
      }
    } else {
      const layMap = JSON.parse(orderFlow.orderConfig.laySlip);
      const { "Remnant Lay": RLay, ...restLay } = layMap;
      layList = Object.keys(restLay).flatMap(key => restLay[key]);
      if (orderFlow.orderConfig.reGeneratedLaySlip) {
        layList = [...layList, ...JSON.parse(orderFlow.orderConfig.reGeneratedLaySlip)];
      } else if (RLay) {
        layList = [...layList, ...RLay];
      }
    }
    if (!layList || layList.length === 0) return;

    const extraRolls = rollList
      ?.filter(r => r.rollType === RollType.EXTRA)
      .map(r => r.rollNumber)
      .map(e => e?.split("-")[0]);

    let markers = new Set();
    let markerNo = 1;
    layList
      .sort((a, b) => a.layNo - b.layNo)
      .forEach(lay => {
        const marker = lay.marker;
        startY = doc.autoTable.previous.finalY;
        if (!markers.has(lay.marker.id)) {
          if (pageHeight - startY < minReqHeight * 2) {
            doc.addPage();
            startY = 10;
          }
          const plies = SpreadingType.FACE_ONE_WAY !== orderFlow.orderConfig.spreadingType ? 2 * marker.totalPlies : marker.totalPlies;

          const sizeGroup = getDistinctValues(marker.name?.split(", ").map(e => e.split("_")[0]));
          const markerName = marker.name
            ?.split(", ")
            .map(e => e.split("_")[1])
            .join(", ");
          const flowMarker = orderFlow?.markerList?.find(m => m.id === marker?.id);
          const markerGroupId = flowMarker?.groupId;
          const markerGroup = orderFlow?.groupList?.find(e => e.id === markerGroupId);
          const isCPL = !isEmpty(setting?.others) && Array.isArray(setting.others) && setting.others.includes(MiscType.CPL);
          const markerWidth = isCPL ? `${toFixed(markerGroup?.widthAxis * 0.01 * (100 + flowMarker?.cplWeftShrPercent), 1)} cm` : "";

          let header = getHeader({ dataType: DataType.MARKER, setting });
          let data = [
            {
              ...marker,
              layLength: `${marker?.layLength} m`,
              markerLength: !isEmpty(marker?.markerLength) ? `${marker?.markerLength} m` : undefined,
              width: `${markerGroup?.widthAxis || 0} cm`,
              layWidth: `${markerGroup?.widthAxis || 0} cm`,
              markerWidth,
              shrinkage: `${isEmpty(markerGroup?.patternNumber) ? "" : `${markerGroup.patternNumber} - `}${marker.shrinkage}`,
              sizeGroup,
              marker: markerName,
              way: marker.way + " way",
              plies
            }
          ];
          if (isEmpty(marker?.markerLength)) {
            header = header.filter(h => h.dataKey !== "markerLength");
          }

          const flowInfoList = getCutOrderFlowInfoList(order, orderFlow);
          const sizeBreakupList = flowInfoList.flatMap(fi => fi.fpoSizeBreakupList || []);
          const haveSizeGroup = getDistinctValues(sizeBreakupList.map(sb => sb.sizeGroup))?.length;
          if (!haveSizeGroup) {
            header = header.filter(h => h.dataKey !== "sizeGroup");
          }
          header = header.map(h => ({ ...h, title: lodashGet(messages, `resources.rollPlan.fields.layslip.${h.dataKey}`) || h.title }));

          const markerTitle = "Marker #" + (marker.markerNo || markerNo) + `${lay.layType === REMNANT_LAY ? "R" : ""}`;

          autoTableOptions = {
            body: data,
            columns: header,
            startY: startY + 10,
            headStyles: { fillColor: [100, 175, 200], lineColor: 100 }
          };

          doc.text(markerTitle, 7, startY + 7);
          addTable(doc, autoTableOptions, { tableWidth });

          startY = doc.autoTable.previous.finalY;
          markers.add(marker.id);
          markerNo++;
        } else {
          if (pageHeight - startY < minReqHeight) {
            doc.addPage();
            startY = 10;
          }
        }

        let header = getHeader({ dataType: DataType.LAY, orderFlow, rollFields, print: true });
        const data = getData({ dataType: DataType.LAY, lay, extraRolls, orderFlow, rollList });
        if (data.every(e => isEmpty(e.supplierRollNo)) || !rollFields.includes("supplierRollNo")) {
          header = header.filter(h => h.dataKey !== "supplierRollNo");
        }
        if (data.every(e => isEmpty(e.rollWeight))) {
          header = header.filter(h => h.dataKey !== "rollWeight");
        }
        if (data.every(e => isEmpty(e.gsm))) {
          header = header.filter(h => h.dataKey !== "gsm");
        }

        if (type === rollPlaneType.INCLUDING_QC_REMARKS) {
          header = [...header, { dataKey: "userRemark", title: "QC Remarks" }].filter(
            e => !["aPly", "aBalance", "causeDeviation"].includes(e.dataKey)
          );
        }

        autoTableOptions = {
          startY: startY + 10,
          body: data,
          columns: header.map(c => ({ dataKey: c.dataKey, header: c.title })),
          headStyles: { halign: "center" },
          bodyStyles: { halign: "center" }
        };

        doc.text("Lay #" + lay.layNo, 7, startY + 7);
        addTable(doc, autoTableOptions, { tableWidth, colWidthMap: { rollLength: 7, rollWeight: 7 } });
      });

    doc.putTotalPages(totalPagesExp);
    const filename = `ROLL PLAN ${order.customer.name} ${order.poRef}${layTab ? `, ${layTab === LayType.ALL_MIX ? "Main Lay" : layTab}` : ""}.pdf`;
    doc.save(filename);
  }
};

// const flowColumns = [
//   { dataKey: "customerFlowRef", header: "Customer Flow Ref" },
//   { dataKey: "flowRef", header: "Flow Ref" },
//   { dataKey: "productName", header: "Product" },
//   { dataKey: "style", header: "Style" },
//   { dataKey: "color", header: "Color" },
//   { dataKey: "partName", header: "Part" },
//   { dataKey: "lot", header: "Lot" },
//   { dataKey: "orderQty", header: "Order Qty" },
//   { dataKey: "extra", header: "Extra Cut (%)" },
//   { dataKey: "inseam", header: "Inseam" },
//   { dataKey: "destination", header: "Destination" },
//   { dataKey: "exFactoryDate", header: "Delivery Date" }
// ];

export const onPrintLay = ({
  order,
  orderFlow,
  lay,
  tenant,
  flowInfoFields = [],
  orderFields = [],
  rollFields = [],
  i18nMessages = {},
  unit = {}
}) => {
  var doc = new jsPDF();
  // Header
  setDefault(doc, "Planned Layslip", tenant, null, unit);

  doc.setFontSize(11);
  let startY = 15;

  const flowInfoList = order.flowInfoList?.filter(fi => orderFlow?.flowInfoIds?.includes(fi.id));
  ////// Print Order Info //////////////
  const orderInfo = layOrderInfoData(order, orderFlow);
  const fields = [...orderFields, ...flowInfoFields, "partName", "lot"];
  let columns = orderInfo.columns
    .filter(c => fields.includes(c.dataKey))
    .map(c => ({
      ...c,
      header: orderFields.includes(c.dataKey)
        ? lodashGet(i18nMessages, `resources.orders.fields.${c.dataKey}`, c.header)
        : lodashGet(i18nMessages, `resources.orders.fields.flowInfo.${c.dataKey}`, c.header)
    }));

  const tableWidth = doc.internal.pageSize.getWidth() - 14;

  var autoTableOptions = {
    startY: startY + 17,
    body: orderInfo.body,
    columns
  };

  doc.text("Order Details", 7, startY + 14);
  addTable(doc, autoTableOptions, { tableWidth });

  ////// Print Lay Details //////////////
  const markerInfo = getMarkerInfo(lay, orderFlow);
  columns = markerInfo.columns.map(c => ({ ...c, header: lodashGet(i18nMessages, `resources.rollPlan.fields.layslip.${c.dataKey}`, c.header) }));

  startY = doc.autoTable.previous.finalY;

  autoTableOptions = {
    startY: startY + 10,
    body: markerInfo?.body,
    columns
  };

  doc.text("Marker Info", 7, startY + 7);
  addTable(doc, autoTableOptions, { tableWidth, minCharLen: 5 });

  ////// Print Other Lay Details (Blank in this case) //////////////
  const spreadingInfo = getSpreadingInfo(lay);

  startY = doc.autoTable.previous.finalY;

  autoTableOptions = {
    startY: startY + 10,
    body: spreadingInfo?.body,
    columns: spreadingInfo?.columns,
    headStyles: { halign: "center" }
  };

  doc.text("Spreading Info", 7, startY + 7);
  addTable(doc, autoTableOptions, { tableWidth });

  ////// Print Lay Slip with Planned Ply and Balance //////////////

  const styleCount = getDistinctValues(flowInfoList.map(fi => fi.style)).length;
  const colorCount = getDistinctValues(flowInfoList.map(fi => fi.color)).length;
  const layData = getLayData(lay, rollFields);
  columns = layData.columns.filter(c => c.dataKey !== "style" || styleCount > 1).filter(c => c.dataKey !== "color" || colorCount > 1);

  startY = doc.autoTable.previous.finalY;

  autoTableOptions = {
    body: layData?.body,
    columns,
    startY: startY + 10,
    headStyles: { halign: "center" },
    bodyStyles: { halign: "center" }
  };

  doc.text("Layslip", 7, startY + 7);
  addTable(doc, autoTableOptions, { tableWidth, colWidthMap: { weight: 7, length: 7 } });

  startY = doc.autoTable.previous.finalY;

  doc.text("QC Name and Signature", 7, startY + 20, "left");
  doc.text("Cutting In-charge Signature", doc.internal.pageSize.getWidth() - 7, startY + 20, "right");

  //TODO: Rename File Name of the PDF.
  const filename = `LAY SLIP - ${order.customer.name} ${order.poRef}.pdf`;

  doc.putTotalPages(totalPagesExp);
  doc.save(filename);
};

const getLayData = (lay, rollFields) => {
  if (isEmpty(lay)) return { columns: [], body: [] };

  let laySlipInfo = { columns: [], body: [] };
  let columnList = [
    { dataKey: "serialNo", header: "Sl. No." },
    { dataKey: "rollNo", header: "F. Roll No" },
    { dataKey: "supplierRollNo", header: "S. Roll No" },
    { dataKey: "style", header: "Style" },
    { dataKey: "color", header: "Style Color" },
    { dataKey: "weight", header: "Weight" },
    { dataKey: "length", header: "Length" },
    { dataKey: "width", header: "Width" },
    { dataKey: "shade", header: "Shade" },
    { dataKey: "plannedPly", header: "Planned Plies" },
    { dataKey: "plannedBalance", header: "Planned Balance" },
    { dataKey: "next", header: "Next" },
    { dataKey: "actualPlies", header: "Actual Plies" },
    { dataKey: "actualBalance", header: "Actual Balance" },
    { dataKey: "reason", header: "Reason" }
  ];

  const rowList = lay?.rowList || [];
  const rows = rowList.sort(cbLayRowSort).map(row => ({
    ...row,
    // plannedBalance: `${row.plannedBalance}${!row.next && row.plannedBalance > 1 && row.plannedBalance < cu ? " *" : ""}`,
    plannedBalance: `${row.plannedBalance}`,
    supplierRollNo: !isEmpty(row.supplierRollNo) ? `${row.supplierRollNo}` : "",
    rollNo: `${row.rollNo}`,
    layLength: lay.marker?.layLength,
    length: `${toFixedValue(row.currLength)} m (${toFixedValue(row.length)} m)`,
    weight: !isEmpty(row.currWeight) && !isEmpty(row.weight) ? `${toFixedValue(row.currWeight, 3)} kg (${toFixedValue(row.weight, 3)} kg)` : "",
    next: row.next || ""
  }));

  if (rows.every(e => isEmpty(e.supplierRollNo)) || !rollFields.includes("supplierRollNo")) {
    columnList = columnList.filter(c => c.dataKey !== "supplierRollNo");
  }
  if (rows.every(e => isEmpty(e.weight) && isEmpty(e.currWeight))) {
    columnList = columnList.filter(c => c.dataKey !== "weight");
  }

  const keys = ["currLength", "currWeight", "plannedPly"];
  const totalRow = keys.reduce(
    (acc, key) => ({
      ...acc,
      [key]: toFixed(
        rows.reduce((acc, row) => acc + parseFloat(row[key] || 0), 0),
        0
      )
    }),
    {}
  );
  rows.push({
    ...totalRow,
    length: `${toFixed(totalRow.currLength, 1)} m`,
    weight: totalRow.currWeight > 0 ? `${toFixed(totalRow.currWeight, 1)} kg` : "",
    rollNo: "Total"
  });

  laySlipInfo = { columns: columnList, body: rows };
  return laySlipInfo;
};

const getSpreadingInfo = lay => {
  if (isEmpty(lay)) return { columns: [], body: [] };
  let columnList = [
    { dataKey: "layNo", header: "Lay No" },
    // { dataKey: "date", header: "Date" },
    { dataKey: "tableNo", header: "Table No" },
    { dataKey: "cutNo", header: "Cut No" },
    { dataKey: "prodType", header: "Production Type" },
    { dataKey: "spreaderName", header: "Spreader Name" },
    { dataKey: "startTime", header: "Start Time" },
    { dataKey: "endTime", header: "End Time" }
  ];

  let body = [{ layNo: lay.layNo }];

  return { columns: columnList, body: body };
};

const getMarkerInfo = (lay, orderFlow) => {
  if (isEmpty(lay) || isEmpty(orderFlow?.markerList)) return { columns: [], body: [] };

  const marker = orderFlow.markerList.find(m => m.id === lay.marker?.id) || {};
  const setting = orderFlow.orderConfig?.setting || {};

  const sizeRatioList = marker?.sizeRatioList || [];
  const sizeList = sizeRatioList.sort((a, b) => a.serialNo - b.serialNo).map(sr => ({ dataKey: sr.sizeKey, header: sr.size }));

  let columnList = [
    { dataKey: "sizeGroup", header: "Size Group" },
    { dataKey: "markerNo", header: "Marker No" },
    ...sizeList,
    { dataKey: "way", header: "Way" },
    { dataKey: "markerLength", header: "Marker \nLength" },
    { dataKey: "layLength", header: "Lay \nLength" },
    { dataKey: "width", header: "Width" },
    { dataKey: "markerWidth", header: "Marker \nWidth" },
    { dataKey: "layWidth", header: "Lay \nWidth" },
    { dataKey: "pattern", header: "Pattern" },
    { dataKey: "csv", header: "CSV" }
  ];

  const isCPL = !isEmpty(setting?.others) && Array.isArray(setting.others) && setting.others.includes(MiscType.CPL);
  if (isCPL) {
    columnList = columnList.filter(c => c.dataKey !== "width");
  } else {
    columnList = columnList.filter(c => !["markerWidth", "layWidth"].includes(c.dataKey));
  }

  const sizeGroup = getDistinctValues(marker.name?.split(", ").map(e => e.split("_")[0]));
  let pattern = "";
  if (!isEmpty(marker.group?.warpPattern) && !isEmpty(marker.group?.weftPattern)) {
    pattern = `${marker.group?.patternNumber || ""} (L${toFixedSigned(marker.group.warpPattern, 1)}% W${toFixedSigned(
      marker.group.weftPattern,
      1
    )}%)`;
  }

  const markerWidth = isCPL ? `${toFixed(marker.group?.widthAxis * 0.01 * (100 + marker.cplWeftShrPercent), 1)} cm` : "";

  const body = [
    {
      sizeGroup,
      markerNo: `M${marker.markerNo}`,
      way: marker?.noOfWays,
      markerLength: `${toFixedValue(marker.length)} m`,
      layLength: `${toFixedValue(marker.layLength)} m`,
      width: `${marker.group?.widthAxis} cm`,
      markerWidth,
      layWidth: `${marker.group?.widthAxis} cm`,
      pattern,
      csv: marker.group?.csv,
      ...sizeRatioList.reduce((acc, sr) => ({ ...acc, [sr.sizeKey]: sr.ratio }), {})
    }
  ];
  return { columns: columnList, body: body };
};

// const layFlowDetails = (order, orderFlow) => {
//   let orderInfo = { columns: [], body: [] };
//   let columnList = flowColumns;

//   const flowInfoList = getCutOrderFlowInfoList(order, orderFlow);

//   const body = flowInfoList?.map(flowInfo => {
//     return {
//       customerFlowRef: flowInfo?.customerFlowRef,
//       flowRef: flowInfo?.flowRef,
//       productName: flowInfo?.product?.name,
//       style: flowInfo?.style,
//       color: flowInfo?.color,
//       lot: `Lot ${orderFlow.lot}`,
//       partName: orderFlow.part,
//       orderQty: flowInfo?.orderQty,
//       extra: !isEmpty(flowInfo?.extra) ? flowInfo?.extra : "",
//       inseam: !isEmpty(flowInfo?.inseam) ? flowInfo?.inseam : "",
//       destination: !isEmpty(flowInfo?.destination) ? flowInfo?.destination : "",
//       exFactoryDate: !isEmpty(flowInfo?.exFactoryDate) ? format(flowInfo?.exFactoryDate, "dd MMM yy") : ""
//     };
//   });

//   orderInfo = { columns: columnList, body: body };
//   return orderInfo;
// };

const layOrderInfoData = (order, orderFlow) => {
  const flowInfoList = getCutOrderFlowInfoList(order, orderFlow);

  let columnList = [
    { dataKey: "poRef", header: "PO Ref" },
    { dataKey: "customerPoRef", header: "Customer PO Ref." },
    { dataKey: "customerName", header: "Customer" },
    { dataKey: "productName", header: "Product" },
    { dataKey: "style", header: "Style Name" },
    { dataKey: "styleNo", header: "Style No" },
    { dataKey: "color", header: "Style Color" },
    { dataKey: "partName", header: "Part" },
    { dataKey: "lot", header: "Lot" },
    { dataKey: "inseam", header: "Inseam" },
    { dataKey: "destination", header: "Destination" },
    { dataKey: "deliveryDate", header: "Del. Date" },
    { dataKey: "flowRef", header: "Line ID" },
    { dataKey: "customerFlowRef", header: "BPO" }
  ];

  //** Exclude Fields if value is empty in each flowInfo **//
  if (flowInfoList.every(fi => isEmpty(fi.styleNo))) {
    columnList = columnList.filter(c => c.dataKey !== "styleNo");
  }
  if (flowInfoList.every(fi => isEmpty(fi.fit))) {
    columnList = columnList.filter(c => c.dataKey !== "fit");
  }
  if (flowInfoList.every(fi => isEmpty(fi.inseam))) {
    columnList = columnList.filter(c => c.dataKey !== "inseam");
  }
  if (flowInfoList.every(fi => isEmpty(fi.destination))) {
    columnList = columnList.filter(c => c.dataKey !== "destination");
  }
  if (flowInfoList.every(fi => isEmpty(fi.deliveryDate))) {
    columnList = columnList.filter(c => c.dataKey !== "deliveryDate");
  }
  if (flowInfoList.every(fi => isEmpty(fi.flowRef))) {
    columnList = columnList.filter(c => c.dataKey !== "flowRef");
  }
  if (flowInfoList.every(fi => isEmpty(fi.customerFlowRef))) {
    columnList = columnList.filter(c => c.dataKey !== "customerFlowRef");
  }

  const body = flowInfoList.map(fi => ({
    poRef: order.poRef,
    customerName: order.customer?.name,
    seasonName: order.season?.name,
    productName: fi.product?.name,
    style: fi.style,
    styleNo: fi.styleNo,
    color: fi.color,
    partName: orderFlow.part,
    lot: orderFlow.lotValue,
    inseam: fi.inseam,
    destination: fi.destination,
    deliveryDate: fi.deliveryDate,
    flowRef: fi.flowRef,
    customerFlowRef: fi.customerFlowRef,
    orderQty: fi.orderQty,
    extra: fi.extra
  }));

  return { columns: columnList, body };
};

// const orderInfoData = (order, orderFlow) => {
//   let orderInfo = {};
//   if (order) {
//     let { customer, customerPoRef, poRef, season, totalQty } = order;
//     const header = ["Customer", "Customer PO Ref", "PO Ref", "Part", "Lot", "Colors", "Season", "Style", "Order Qty"];
//     const data = [
//       [customer.name, customerPoRef || "", poRef, orderFlow.part, "Lot-" + orderFlow.lot, orderFlow.colors, season.name, orderFlow.styles, totalQty]
//     ];
//     orderInfo = { header, data };
//   }
//   return orderInfo;
// };
